import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/apiService';
import {
  Plus, RefreshCw, RotateCcw, Save, CheckCircle, XCircle,
  Shield, Lock, Unlock, Tag,
  Trash2, ToggleLeft, ToggleRight, X, Zap, Cpu, AlertTriangle,
  Stethoscope, Megaphone, Scale, HelpCircle, Check,
} from 'lucide-react';

interface Category {
  slug: string;
  name: string;
  isActive: boolean;
  isDeletable: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface AvailableModel {
  id: string;
  name: string;
  provider: string;
  ranked_position?: number;
}

interface CardState {
  trueRules: string[];
  falseRules: string[];
  isCustomized: boolean;
  rulesLoaded: boolean;
  saving: boolean;
  error: string;
  success: string;
  // Model assignment
  tagModelIds: string[];
  tagModelsLoaded: boolean;
  tagModelsSaving: boolean;
  tagModelsSuccess: string;
  tagModelsError: string;
}

const defaultCardState = (): CardState => ({
  trueRules: [''],
  falseRules: [''],
  isCustomized: false,
  rulesLoaded: false,
  saving: false,
  error: '',
  success: '',
  tagModelIds: [],
  tagModelsLoaded: false,
  tagModelsSaving: false,
  tagModelsSuccess: '',
  tagModelsError: '',
});

// ── Privacy-specific parse / combine ─────────────────────────────────────────
function splitRulesToArray(block: string): string[] {
  const lines = block.split('\n')
    .map(l => l.trim().replace(/^[-*•]\s*/, ''))
    .filter(l => l.length > 0);
  return lines.length > 0 ? lines : [''];
}

function parsePrivacyRules(rulesText: string): { trueRules: string[]; falseRules: string[] } {
  const falseMatch = rulesText.match(/EXPLICITLY NOT PRIVATE[^:]*:\s*([\s\S]+?)(?:\n\n|IMPORTANT:)/);
  const trueMatch  = rulesText.match(/to true when the query involves:\s*([\s\S]*?)\s*(?:FINAL ENFORCEMENT:|$)/);
  return {
    trueRules:  trueMatch  ? splitRulesToArray(trueMatch[1])  : [''],
    falseRules: falseMatch ? splitRulesToArray(falseMatch[1]) : [''],
  };
}

function combinePrivacyRules(trueRules: string[], falseRules: string[]) {
  const trueLines  = trueRules.filter(r => r.trim()).map(r => `  - ${r}`).join('\n');
  const falseLines = falseRules.filter(r => r.trim()).map(r => `- ${r}`).join('\n');
  return `PRIVACY DETECTION RULES:

⚠️ CRITICAL OVERRIDE INSTRUCTION - READ THIS FIRST:
You are FORBIDDEN from using your training data or general knowledge about privacy/sensitivity. The ONLY criteria that matter are explicitly listed below. Ignore all other considerations, including what you were trained to consider private.

🚫 WHAT IS NOT PRIVATE - Read This Before Anything Else:
The following are EXPLICITLY NOT PRIVATE and must ALWAYS return requires_privacy=false:
${falseLines}

IMPORTANT: You MUST mark "requires_privacy" as true ONLY if the query EXPLICITLY matches one or more of the criteria listed below. Do NOT use your general knowledge to infer privacy requirements. If the query does not match the specific criteria below, mark "requires_privacy" as false, regardless of whether the content seems sensitive.

✅ WHAT IS PRIVATE - The ONLY things that trigger privacy:
- Set "requires_privacy" to true when the query involves:
${trueLines}

FINAL ENFORCEMENT:
- If the query matches the "NOT PRIVATE" section above → requires_privacy = FALSE
- Only if the query matches the "WHAT IS PRIVATE" section → requires_privacy = TRUE
- When in doubt → requires_privacy = FALSE
- Privacy is a REQUIREMENT, not a task type - the actual task type should still be determined (coding, reasoning, etc.)`;
}

// ── Custom-tag parse / combine (structured JSON) ──────────────────────────────
function parseCustomTagRules(rulesText: string): { trueRules: string[]; falseRules: string[] } {
  try {
    const parsed = JSON.parse(rulesText);
    if (parsed && Array.isArray(parsed.true) && Array.isArray(parsed.false)) {
      return {
        trueRules: parsed.true.length > 0 ? parsed.true : [''],
        falseRules: parsed.false.length > 0 ? parsed.false : [''],
      };
    }
  } catch {
    // Legacy plain-text — put it in the first true rule so it's not lost
    return { trueRules: [rulesText.trim()], falseRules: [''] };
  }
  return { trueRules: [''], falseRules: [''] };
}

function serializeCustomTagRules(trueRules: string[], falseRules: string[]): string {
  return JSON.stringify({
    true: trueRules.filter(r => r.trim()),
    false: falseRules.filter(r => r.trim()),
  });
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CategoryRules() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [cardStates, setCardStates] = useState<Record<string, CardState>>({});
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [activeTab, setActiveTab] = useState<'configure' | 'test'>('configure');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [tagModelModalSlug, setTagModelModalSlug] = useState<string | null>(null);
  const [tagModelModalDraft, setTagModelModalDraft] = useState<string[]>([]);
  const [tagModelModalSearch, setTagModelModalSearch] = useState('');

  // Load available models for tag assignment
  useEffect(() => {
    apiService.getAvailableModelsForList().then((data: any) => {
      const models = (data?.data || data || [])
        .map((m: any) => ({ id: m.id, name: m.name, provider: m.provider, ranked_position: m.ranked_position }))
        .sort((a: AvailableModel, b: AvailableModel) => (a.ranked_position || 999) - (b.ranked_position || 999));
      setAvailableModels(models);
    }).catch(() => {});
  }, []);

  // Test center
  const [centerQuery, setCenterQuery] = useState('');
  const [centerResult, setCenterResult] = useState<any>(null);
  const [centerTesting, setCenterTesting] = useState(false);
  const [centerError, setCenterError] = useState('');
  const [tdModel, setTdModel] = useState<{ name: string; rank: number } | null>(null);

  useEffect(() => {
    apiService.get('/portal/models/custom').then((models: any[]) => {
      const privacyTd = models
        .filter((m: any) => m.taskDeterminationModel === true && m.isPrivacyCompliant === true)
        .sort((a: any, b: any) => (a.ranked_position || 999) - (b.ranked_position || 999));
      if (privacyTd.length > 0) {
        setTdModel({ name: privacyTd[0].name, rank: privacyTd[0].ranked_position || 999 });
      }
    }).catch(() => {});
  }, []);

  const handleCenterTest = async () => {
    if (!centerQuery.trim()) return;
    setCenterTesting(true);
    setCenterError('');
    setCenterResult(null);
    try {
      const data = await apiService.testAllCategoryRules(centerQuery);
      if (data.success) {
        setCenterResult(data.result);
      } else {
        setCenterError(data.message || 'Test failed');
      }
    } catch (err: any) {
      setCenterError(err.response?.data?.message || 'Failed to run test');
    } finally {
      setCenterTesting(false);
    }
  };

  // Add modal
  const [categorySearch, setCategorySearch] = useState('');

  // Add modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      setPageLoading(true);
      setPageError('');
      const data = await apiService.listCategories();
      const cats: Category[] = (data.categories || []).filter((c: Category) => c.slug && c.name);
      setCategories(cats);
      setCardStates(prev => {
        const next = { ...prev };
        cats.forEach(c => { if (!next[c.slug]) next[c.slug] = defaultCardState(); });
        return next;
      });
    } catch (err: any) {
      setPageError(err.response?.data?.message || 'Failed to load categories');
    } finally {
      setPageLoading(false);
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  // Auto-select first category once loaded
  useEffect(() => {
    if (!selectedSlug && categories.length > 0) {
      const first = categories[0];
      setSelectedSlug(first.slug);
      loadRules(first.slug);
      loadTagModels(first.slug);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const updateCard = (slug: string, patch: Partial<CardState>) =>
    setCardStates(prev => ({ ...prev, [slug]: { ...prev[slug], ...patch } }));

  const loadRules = async (slug: string, force = false) => {
    const cs = cardStates[slug];
    if (!force && cs?.rulesLoaded) return;
    try {
      const data = await apiService.getCategoryRules(slug);
      if (data.success) {
        if (slug === 'privacy') {
          const { trueRules, falseRules } = parsePrivacyRules(data.rules);
          updateCard(slug, { trueRules, falseRules, isCustomized: data.isCustomized ?? false, rulesLoaded: true });
        } else {
          const { trueRules, falseRules } = parseCustomTagRules(data.rules);
          updateCard(slug, { trueRules, falseRules, isCustomized: data.isCustomized ?? false, rulesLoaded: true });
        }
      }
    } catch {
      updateCard(slug, { error: 'Failed to load rules', rulesLoaded: true });
    }
  };

  const loadTagModels = async (slug: string, force = false) => {
    if (slug === 'privacy') return;
    const cs = cardStates[slug];
    if (!force && cs?.tagModelsLoaded) return;
    try {
      const data = await apiService.getTagModels(slug);
      const modelIds = (data as any)?.data?.modelIds || (data as any)?.modelIds || [];
      updateCard(slug, { tagModelIds: modelIds, tagModelsLoaded: true });
    } catch {
      updateCard(slug, { tagModelIds: [], tagModelsLoaded: true });
    }
  };

  const handleSelectCategory = async (slug: string) => {
    setSelectedSlug(slug);
    await loadRules(slug);
    await loadTagModels(slug);
  };

  const openTagModelModal = (cat: Category) => {
    const cs = cardStates[cat.slug];
    setTagModelModalDraft(cs?.tagModelIds ? [...cs.tagModelIds] : []);
    setTagModelModalSearch('');
    setTagModelModalSlug(cat.slug);
  };

  const handleSaveTagModels = async () => {
    if (!tagModelModalSlug) return;
    updateCard(tagModelModalSlug, { tagModelsSaving: true, tagModelsError: '', tagModelsSuccess: '' });
    try {
      await apiService.putTagModels(tagModelModalSlug, tagModelModalDraft);
      updateCard(tagModelModalSlug, { tagModelIds: tagModelModalDraft, tagModelsSaving: false, tagModelsSuccess: 'Model assignments saved!' });
      setTagModelModalSlug(null);
      setTimeout(() => updateCard(tagModelModalSlug!, { tagModelsSuccess: '' }), 3000);
    } catch (err: any) {
      updateCard(tagModelModalSlug, { tagModelsSaving: false, tagModelsError: err.response?.data?.message || 'Failed to save model assignments' });
    }
  };

  const handleToggleActive = async (cat: Category) => {
    const nextActive = !cat.isActive;
    try {
      await apiService.toggleCategoryActive(cat.slug, nextActive);
      setCategories(prev => prev.map(c => c.slug === cat.slug ? { ...c, isActive: nextActive } : c));
    } catch (err: any) {
      setPageError(err.response?.data?.message || 'Failed to update category');
    }
  };

  const handleSaveRules = async (cat: Category) => {
    const cs = cardStates[cat.slug];
    updateCard(cat.slug, { saving: true, error: '', success: '' });
    try {
      const combined =
        cat.slug === 'privacy'
          ? combinePrivacyRules(cs.trueRules, cs.falseRules)
          : serializeCustomTagRules(cs.trueRules, cs.falseRules);
      const data = await apiService.updateCategoryRules(cat.slug, combined);
      if (data.success) {
        updateCard(cat.slug, { success: 'Rules saved!', isCustomized: true, saving: false });
        setTimeout(() => updateCard(cat.slug, { success: '' }), 3000);
      }
    } catch (err: any) {
      updateCard(cat.slug, { error: err.response?.data?.message || 'Failed to save rules', saving: false });
    }
  };

  const handleResetToDefault = async (slug: string) => {
    if (!window.confirm('Reset to default rules? This cannot be undone.')) return;
    updateCard(slug, { saving: true, error: '', success: '' });
    try {
      await apiService.resetCategoryToDefault(slug);
      updateCard(slug, { saving: false, rulesLoaded: false });
      await loadRules(slug, true);
      updateCard(slug, { success: 'Reset to default!', isCustomized: false });
      setTimeout(() => updateCard(slug, { success: '' }), 3000);
    } catch (err: any) {
      updateCard(slug, { error: err.response?.data?.message || 'Failed to reset', saving: false });
    }
  };

  const handleDeleteCategory = async (slug: string) => {
    if (!window.confirm(`Delete the "${slug}" category? This cannot be undone.\n\nAny API keys using this tag will have it automatically removed.`)) return;
    try {
      await apiService.deleteCategory(slug);
      setCategories(prev => prev.filter(c => c.slug !== slug));
      setCardStates(prev => { const n = { ...prev }; delete n[slug]; return n; });
      if (selectedSlug === slug) setSelectedSlug(null);
    } catch (err: any) {
      setPageError(err.response?.data?.message || 'Failed to delete category');
    }
  };

  // Add modal helpers
  const autoSlug = (name: string) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 32);

  const handleNameChange = (val: string) => {
    setNewName(val);
    setNewSlug(autoSlug(val));
  };

  const handleAddCategory = async () => {
    if (!newName.trim()) { setAddError('Tag name is required'); return; }
    setAdding(true);
    setAddError('');
    try {
      const createdSlug = newSlug.trim();
      await apiService.createCategory(newName.trim(), createdSlug);
      setAddModalOpen(false);
      setNewName('');
      setNewSlug('');
      await loadCategories();
      setSelectedSlug(createdSlug);
      await loadRules(createdSlug);
      await loadTagModels(createdSlug);
    } catch (err: any) {
      setAddError(err.response?.data?.message || 'Failed to create category');
    } finally {
      setAdding(false);
    }
  };

  if (pageLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 256 }}>
        <div className="pk-skel" style={{ width: 32, height: 32, borderRadius: '50%' }} />
      </div>
    );
  }

  const selectedCat = categories.find(c => c.slug === selectedSlug) ?? null;
  const selectedState = selectedSlug ? (cardStates[selectedSlug] || defaultCardState()) : null;

  return (
    <div>
      {/* Page Header */}
      <div className="pk-page-head">
        <div className="col">
          <h1 className="pk-h1">Category Rules</h1>
          <p className="pk-sub">Manage detection rules for Privacy and your custom tags. Active categories are evaluated on every request.</p>
        </div>
        <div className="actions">
          <button type="button" onClick={loadCategories} className="pk-btn pk-btn-outline">
            <RefreshCw size={16} /> Reload
          </button>
        </div>
      </div>

      {pageError && (
        <div className="pk-note pk-note-danger" style={{ marginBottom: 20 }}>
          <XCircle size={16} />
          <div>{pageError}</div>
        </div>
      )}

      {/* Tabs */}
      <div className="pk-tabs" style={{ marginBottom: 20 }}>
        <button type="button" className={`pk-tab ${activeTab === 'configure' ? 'is-active' : ''}`} onClick={() => setActiveTab('configure')}>
          <Shield size={14} /> Configure Rules
        </button>
        <button type="button" className={`pk-tab ${activeTab === 'test' ? 'is-active' : ''}`} onClick={() => setActiveTab('test')}>
          <Zap size={14} /> Test Center
        </button>
      </div>

      {/* Tab content */}
      <div style={{ minHeight: 620 }}>
      {activeTab === 'configure' && (
        <div style={{ display: 'flex', gap: 20, minHeight: 560 }}>
          {/* Sidebar */}
          <div style={{ width: 224, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input
              type="text"
              placeholder="Search tags..."
              value={categorySearch}
              onChange={e => setCategorySearch(e.target.value)}
              className="pk-input"
            />
            {categories.filter(c =>
              !categorySearch.trim() || c.name.toLowerCase().includes(categorySearch.toLowerCase()) || c.slug.toLowerCase().includes(categorySearch.toLowerCase())
            ).map(cat => (
              <div
                key={cat.slug}
                role="button"
                tabIndex={0}
                onClick={() => handleSelectCategory(cat.slug)}
                onKeyDown={e => e.key === 'Enter' && handleSelectCategory(cat.slug)}
                className={`pk-nav-item group ${selectedSlug === cat.slug ? 'is-active' : ''}`}
                style={{ height: 'auto', minHeight: 44, cursor: 'pointer', border: '1px solid var(--arc-line)' }}
              >
                {cat.slug === 'privacy' ? <Shield size={16} className="pk-i" /> : <Tag size={16} className="pk-i" />}
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  {cat.isDeletable && (
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); handleDeleteCategory(cat.slug); }}
                      className="pk-btn pk-btn-quiet pk-btn-icon"
                      style={{ width: 22, height: 22, color: 'var(--arc-danger)' }}
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: (cat.isActive || cat.slug === 'privacy') ? 'var(--arc-success)' : 'var(--arc-line)' }} />
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => setAddModalOpen(true)}
              className="pk-btn pk-btn-outline"
              style={{ marginTop: 4, borderStyle: 'dashed' }}
            >
              <Plus size={15} /> Add Custom Tag
            </button>
          </div>

          {/* Editor Panel */}
          <div className="pk-card pk-card-flush" style={{ flex: 1, overflow: 'hidden' }}>
            {!selectedCat || !selectedState ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <div style={{ textAlign: 'center' }}>
                  <Tag size={40} style={{ margin: '0 auto 10px', opacity: 0.3, color: 'var(--arc-ink-4)' }} />
                  <p className="pk-small">Select a category to edit its rules</p>
                </div>
              </div>
            ) : (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Editor header */}
                <div className="pk-card-head">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div className="pk-i-plate">{selectedCat.slug === 'privacy' ? <Shield size={16} /> : <Tag size={16} />}</div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <h2 className="pk-h4">{selectedCat.name}</h2>
                        {selectedState.isCustomized && <span className="pk-badge pk-badge-warning">Customized</span>}
                        {!selectedState.isCustomized && selectedState.rulesLoaded && <span className="pk-badge">Default</span>}
                      </div>
                      <p className="pk-small" style={{ marginTop: 2 }}>
                        {selectedCat.slug === 'privacy' ? 'Built-in · Cannot delete' : `slug: ${selectedCat.slug}`}
                      </p>
                    </div>
                  </div>
                  <div className="actions">
                    {selectedCat.slug === 'privacy' ? (
                      <span className="pk-badge pk-badge-success" title="Privacy detection is always active">
                        <ToggleRight size={13} /> Always Active
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleToggleActive(selectedCat)}
                        className={`pk-badge ${selectedCat.isActive ? 'pk-badge-success' : ''}`}
                        style={{ border: 0, cursor: 'pointer' }}
                      >
                        {selectedCat.isActive ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
                        {selectedCat.isActive ? 'Active' : 'Disabled'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Editor body */}
                <div className="pk-card-body" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {selectedState.error && (
                    <div className="pk-note pk-note-danger">
                      <XCircle size={16} />
                      <div style={{ flex: 1 }}>{selectedState.error}</div>
                    </div>
                  )}
                  {selectedState.success && (
                    <div className="pk-note pk-note-accent" style={{ background: 'var(--arc-success-soft)' }}>
                      <CheckCircle size={16} style={{ color: 'var(--arc-success)' }} />
                      <div style={{ flex: 1 }}>{selectedState.success}</div>
                    </div>
                  )}

                  {!selectedState.rulesLoaded ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                      <div className="pk-skel" style={{ width: 24, height: 24, borderRadius: '50%' }} />
                    </div>
                  ) : (
                    <>
                      {/* TRUE section */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Lock size={15} style={{ color: 'var(--arc-warning)' }} />
                          <span className="pk-badge pk-badge-warning">Set {selectedCat.slug} = true when</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {selectedState.trueRules.map((rule, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span className="pk-mono" style={{ color: 'var(--arc-ink-4)', fontSize: 13, flexShrink: 0 }}>*</span>
                              <input
                                type="text"
                                value={rule}
                                onChange={e => {
                                  const next = [...selectedState.trueRules];
                                  next[i] = e.target.value;
                                  updateCard(selectedCat.slug, { trueRules: next });
                                }}
                                className="pk-input"
                                style={{ flex: 1, borderColor: 'var(--arc-warning-soft)', background: 'var(--arc-warning-soft)' }}
                                placeholder={`e.g. query involves ${selectedCat.name.toLowerCase()}-related content`}
                              />
                              {selectedState.trueRules.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => updateCard(selectedCat.slug, { trueRules: selectedState.trueRules.filter((_, j) => j !== i) })}
                                  className="pk-btn pk-btn-quiet pk-btn-icon"
                                  style={{ width: 28, height: 28, flexShrink: 0, color: 'var(--arc-danger)' }}
                                  title="Remove"
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => updateCard(selectedCat.slug, { trueRules: [...selectedState.trueRules, ''] })}
                            className="pk-btn-text"
                            style={{ color: 'var(--arc-warning)' }}
                          >
                            <Plus size={13} /> Add condition
                          </button>
                          {selectedState.trueRules.every(r => !r.trim()) && (
                            <p className="pk-small" style={{ fontStyle: 'italic' }}>
                              No criteria set — this tag won't detect any queries until you add at least one condition.
                            </p>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ flex: 1, height: 1, background: 'var(--arc-line-2)' }} />
                        <span className="pk-label" style={{ fontSize: 10.5 }}>Exclusions</span>
                        <div style={{ flex: 1, height: 1, background: 'var(--arc-line-2)' }} />
                      </div>

                      {/* FALSE section */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Unlock size={15} style={{ color: 'var(--arc-success)' }} />
                          <span className="pk-badge pk-badge-success">Set {selectedCat.slug} = false when</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {selectedState.falseRules.map((rule, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span className="pk-mono" style={{ color: 'var(--arc-ink-4)', fontSize: 13, flexShrink: 0 }}>*</span>
                              <input
                                type="text"
                                value={rule}
                                onChange={e => {
                                  const next = [...selectedState.falseRules];
                                  next[i] = e.target.value;
                                  updateCard(selectedCat.slug, { falseRules: next });
                                }}
                                className="pk-input"
                                style={{ flex: 1, borderColor: 'var(--arc-success-soft)', background: 'var(--arc-success-soft)' }}
                                placeholder="e.g. general questions unrelated to this topic"
                              />
                              {selectedState.falseRules.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => updateCard(selectedCat.slug, { falseRules: selectedState.falseRules.filter((_, j) => j !== i) })}
                                  className="pk-btn pk-btn-quiet pk-btn-icon"
                                  style={{ width: 28, height: 28, flexShrink: 0, color: 'var(--arc-danger)' }}
                                  title="Remove"
                                >
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => updateCard(selectedCat.slug, { falseRules: [...selectedState.falseRules, ''] })}
                            className="pk-btn-text"
                            style={{ color: 'var(--arc-success)' }}
                          >
                            <Plus size={13} /> Add condition
                          </button>
                        </div>
                      </div>

                      {/* Model Assignment (custom tags only) — compact button row */}
                      {selectedCat.slug !== 'privacy' && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid var(--arc-line-2)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Cpu size={15} className="pk-i" />
                            {!selectedState.tagModelsLoaded ? (
                              <span className="pk-small" style={{ fontStyle: 'italic' }}>Loading models…</span>
                            ) : selectedState.tagModelIds.length === 0 ? (
                              <span className="pk-badge pk-badge-warning"><AlertTriangle size={11} /> No models assigned</span>
                            ) : (
                              <span className="pk-small" style={{ fontWeight: 700, color: 'var(--arc-accent)' }}>
                                {selectedState.tagModelIds.length} model{selectedState.tagModelIds.length !== 1 ? 's' : ''} assigned
                              </span>
                            )}
                            {selectedState.tagModelsSuccess && (
                              <span className="pk-small" style={{ color: 'var(--arc-success)', fontWeight: 700 }}>{selectedState.tagModelsSuccess}</span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => openTagModelModal(selectedCat)}
                            disabled={!selectedState.tagModelsLoaded}
                            className="pk-btn pk-btn-outline pk-btn-sm"
                          >
                            <Cpu size={13} /> Assign Models
                          </button>
                        </div>
                      )}

                      {/* Actions */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid var(--arc-line-2)' }}>
                        <div>
                          {selectedCat.slug === 'privacy' && (
                            <button
                              type="button"
                              onClick={() => handleResetToDefault(selectedCat.slug)}
                              disabled={selectedState.saving}
                              className="pk-btn pk-btn-outline"
                            >
                              <RotateCcw size={15} /> Restore to Default
                            </button>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleSaveRules(selectedCat)}
                          disabled={selectedState.saving || selectedState.trueRules.every(r => !r.trim())}
                          className="pk-btn pk-btn-ink"
                        >
                          <Save size={15} />
                          {selectedState.saving ? 'Saving...' : 'Save Rules'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test Tab */}
      {activeTab === 'test' && (
        <TestCenter
          categories={categories}
          query={centerQuery}
          onQueryChange={setCenterQuery}
          onTest={handleCenterTest}
          testing={centerTesting}
          result={centerResult}
          error={centerError}
          tdModel={tdModel}
        />
      )}
      </div>

      {/* Add Custom Tag Modal */}
      {addModalOpen && (
        <div className="pk-scrim" style={{ position: 'fixed', zIndex: 9999 }} onClick={() => { setAddModalOpen(false); setAddError(''); setNewName(''); setNewSlug(''); }}>
          <div className="pk-modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="pk-modal-head" style={{ justifyContent: 'space-between' }}>
              <h3 className="pk-h3">Add Custom Tag</h3>
              <button type="button" onClick={() => { setAddModalOpen(false); setAddError(''); setNewName(''); setNewSlug(''); }} className="pk-btn pk-btn-quiet pk-btn-icon">
                <X size={18} />
              </button>
            </div>

            <div className="pk-modal-body">
              {addError && <div className="pk-note pk-note-danger">{addError}</div>}

              <div className="pk-field">
                <label className="pk-field-label">Tag Name <span className="req">*</span></label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. Marketing"
                  className="pk-input"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
                />
                <p className={newSlug && categories.some(c => c.slug === newSlug) ? 'pk-error' : 'pk-help'} style={{ minHeight: 16 }}>
                  {newSlug ? <>Identifier: <span className="pk-mono">{newSlug}</span>{categories.some(c => c.slug === newSlug) && ' — already exists'}</> : ''}
                </p>
              </div>
            </div>

            <div className="pk-modal-foot">
              <div className="spacer" />
              <button type="button" onClick={() => { setAddModalOpen(false); setAddError(''); setNewName(''); setNewSlug(''); }} className="pk-btn pk-btn-outline">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddCategory}
                disabled={adding || !newName.trim() || categories.some(c => c.slug === newSlug)}
                className="pk-btn pk-btn-ink"
              >
                {adding ? 'Creating...' : 'Create Tag'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Assign Models Modal */}
      {tagModelModalSlug && (() => {
        const modalCat = categories.find(c => c.slug === tagModelModalSlug);
        const cs = cardStates[tagModelModalSlug];
        const filtered = availableModels.filter(m =>
          !tagModelModalSearch.trim() ||
          m.name.toLowerCase().includes(tagModelModalSearch.toLowerCase()) ||
          m.provider.toLowerCase().includes(tagModelModalSearch.toLowerCase())
        );
        return (
          <div className="pk-scrim" style={{ position: 'fixed', zIndex: 9999 }} onClick={() => setTagModelModalSlug(null)}>
            <div className="pk-modal" style={{ maxWidth: 520, maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
              {/* Modal header */}
              <div className="pk-modal-head" style={{ justifyContent: 'space-between' }}>
                <div className="col">
                  <h3 className="pk-h4" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Cpu size={15} style={{ color: 'var(--arc-accent)' }} />
                    Assign Models — <span className="pk-mono" style={{ color: 'var(--arc-accent)' }}>{tagModelModalSlug}</span>
                  </h3>
                  <p className="pk-small">Selected models will be used when this tag fires. Click to toggle.</p>
                </div>
                <button type="button" onClick={() => setTagModelModalSlug(null)} className="pk-btn pk-btn-quiet pk-btn-icon">
                  <X size={18} />
                </button>
              </div>

              {/* Search */}
              <div style={{ padding: '12px 22px', borderBottom: '1px solid var(--arc-line-2)', flexShrink: 0 }}>
                <input
                  type="text"
                  value={tagModelModalSearch}
                  onChange={e => setTagModelModalSearch(e.target.value)}
                  placeholder="Search models…"
                  className="pk-input"
                  autoFocus
                />
              </div>

              {/* Model list */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {filtered.length === 0 ? (
                  <p className="pk-empty-inline pk-small">No models match your search</p>
                ) : (
                  <table className="pk-table">
                    <thead>
                      <tr>
                        <th style={{ width: 34 }} />
                        <th>Name</th>
                        <th>Provider</th>
                        <th>Rank</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(m => {
                        const selected = tagModelModalDraft.includes(m.id);
                        return (
                          <tr
                            key={m.id}
                            onClick={() => setTagModelModalDraft(prev =>
                              selected ? prev.filter(id => id !== m.id) : [...prev, m.id]
                            )}
                            style={{ cursor: 'pointer', background: selected ? 'var(--arc-accent-soft)' : undefined }}
                          >
                            <td>
                              <div style={{
                                width: 16, height: 16, borderRadius: 4, border: '1px solid var(--arc-line)',
                                display: 'grid', placeItems: 'center',
                                background: selected ? 'var(--arc-accent)' : 'var(--arc-paper)', borderColor: selected ? 'var(--arc-accent)' : undefined,
                              }}>
                                {selected && <Check size={11} style={{ color: '#fff' }} />}
                              </div>
                            </td>
                            <td className={selected ? 'strong' : undefined}>{m.name}</td>
                            <td className="pk-small">{m.provider}</td>
                            <td className="pk-small">{m.ranked_position || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Footer */}
              <div className="pk-modal-foot">
                <span className="pk-small">
                  {tagModelModalDraft.length === 0
                    ? 'None selected — tag fires but pool unrestricted'
                    : `${tagModelModalDraft.length} model${tagModelModalDraft.length !== 1 ? 's' : ''} selected`}
                </span>
                <div className="spacer" />
                <button type="button" onClick={() => setTagModelModalSlug(null)} className="pk-btn pk-btn-outline">
                  Cancel
                </button>
                <button type="button" onClick={handleSaveTagModels} disabled={cs?.tagModelsSaving} className="pk-btn pk-btn-ink">
                  <Save size={14} />
                  {cs?.tagModelsSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Test Center ───────────────────────────────────────────────────────────────
interface TestCenterProps {
  categories: Category[];
  query: string;
  onQueryChange: (v: string) => void;
  onTest: () => void;
  testing: boolean;
  result: any;
  error: string;
  tdModel: { name: string; rank: number } | null;
}

const QUICK_EXAMPLES = [
  { label: 'Medical Info', icon: <Stethoscope size={13} />, query: 'I have diabetes and take insulin daily' },
  { label: 'Marketing Brief', icon: <Megaphone size={13} />, query: 'Write a marketing campaign for our Q4 product launch' },
  { label: 'Legal Review', icon: <Scale size={13} />, query: 'Review this NDA clause for compliance issues' },
  { label: 'General Question', icon: <HelpCircle size={13} />, query: 'What is the capital of France?' },
  { label: 'Business Confidential', icon: <Lock size={13} />, query: "Our company's Q4 earnings were $5M, keep it confidential" },
];

function TestCenter({ categories, query, onQueryChange, onTest, testing, result, error, tdModel }: TestCenterProps) {
  const activeCategories = categories.filter(c => c.isActive);

  return (
    <div className="pk-card pk-card-flush pk-card-grad-top">
      <div className="pk-card-head" style={{ alignItems: 'flex-start' }}>
        <div className="col">
          <h2 className="pk-h3" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Zap size={18} style={{ color: 'var(--arc-accent)' }} /> Test Center
          </h2>
          <p className="pk-sub" style={{ marginTop: 4 }}>
            Run one query against all active categories at once — mirrors real-world task determination behaviour.
          </p>
          <p className="pk-small" style={{ marginTop: 6 }}>
            {tdModel
              ? <>The highest-ranked privacy-compliant TD model is used. Detection accuracy varies by model capability — if results seem off, switch to a more capable TD model (e.g. GPT-4o).</>
              : <>No privacy-compliant TD model configured. Add one in Models to enable testing.</>
            }
          </p>
        </div>
        <div className="actions">
          <span className={`pk-badge ${tdModel ? '' : 'pk-badge-warning'}`}>
            {tdModel ? (
              <>TD model: {tdModel.name}{tdModel.rank < 999 && <span className="pk-mono" style={{ marginLeft: 4, textTransform: 'none' }}>rank {tdModel.rank}</span>}</>
            ) : (
              'No privacy TD model configured'
            )}
          </span>
        </div>
      </div>

      <div className="pk-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div>
          <p className="pk-label" style={{ marginBottom: 8 }}>Quick Examples</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {QUICK_EXAMPLES.map(ex => (
              <button key={ex.label} type="button" onClick={() => onQueryChange(ex.query)} className="pk-btn pk-btn-outline pk-btn-sm">
                {ex.icon} {ex.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <input
            type="text"
            value={query}
            onChange={e => onQueryChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && query.trim() && onTest()}
            placeholder="Enter a query to test all active categories…"
            className="pk-input"
            style={{ flex: 1 }}
          />
          <button type="button" onClick={onTest} disabled={testing || !query.trim()} className="pk-btn pk-btn-grad">
            <Zap size={15} />
            {testing ? 'Running…' : 'Run Test'}
          </button>
        </div>

        {error && (
          <div className="pk-note pk-note-danger">
            <XCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {testing && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 0', gap: 12 }}>
            <div className="pk-skel" style={{ width: 32, height: 32, borderRadius: '50%' }} />
            <span className="pk-small" style={{ fontWeight: 700 }}>Running task determination…</span>
          </div>
        )}

        {result && !testing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="pk-grid pk-grid-3">
              <div className="pk-card-inset" style={{ textAlign: 'center' }}>
                <p className="pk-small">Task Type</p>
                <p style={{ fontWeight: 700, color: 'var(--arc-ink)', textTransform: 'capitalize', marginTop: 2 }}>{result.taskType}</p>
              </div>
              <div className="pk-card-inset" style={{ textAlign: 'center' }}>
                <p className="pk-small">Complexity</p>
                <p style={{ fontWeight: 700, color: 'var(--arc-ink)', marginTop: 2 }}>{result.complexity} / 5</p>
              </div>
              <div className="pk-card-inset" style={{ textAlign: 'center' }}>
                <p className="pk-small">Model Used</p>
                <p style={{ fontWeight: 700, color: 'var(--arc-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{result.modelUsed || '—'}</p>
              </div>
            </div>

            <div>
              <p className="pk-label" style={{ marginBottom: 10 }}>Category Results — {activeCategories.length} active</p>
              {activeCategories.length === 0 ? (
                <p className="pk-small" style={{ fontStyle: 'italic' }}>No active categories to display.</p>
              ) : (
                <div className="pk-grid pk-grid-2">
                  {activeCategories.some(c => c.slug === 'privacy') && (
                    <CategoryResultBadge name="Privacy" slug="privacy" fired={result.requiresPrivacy === true} />
                  )}
                  {activeCategories.filter(c => c.slug !== 'privacy').map(cat => (
                    <CategoryResultBadge key={cat.slug} name={cat.name} slug={cat.slug} fired={result.customTags?.[cat.slug] === true} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {!result && !testing && !error && (
          <div className="pk-empty">
            <div className="pk-i-plate"><Zap size={18} /></div>
            <p className="pk-sub">No results yet</p>
            <p className="pk-small">Enter a query above to see how all categories respond</p>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryResultBadge({ name, slug, fired }: { name: string; slug: string; fired: boolean }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px',
        borderRadius: 'var(--arc-r-md)', border: `1px solid ${fired ? 'var(--arc-success)' : 'var(--arc-line)'}`,
        background: fired ? 'var(--arc-success-soft)' : 'var(--arc-bg-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {slug === 'privacy'
          ? <Shield size={15} style={{ color: fired ? 'var(--arc-success)' : 'var(--arc-ink-3)' }} />
          : <Tag size={15} style={{ color: fired ? 'var(--arc-success)' : 'var(--arc-ink-3)' }} />
        }
        <div>
          <span style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--arc-ink)' }}>{name}</span>
          <span className="pk-mono pk-small" style={{ marginLeft: 6 }}>{slug}</span>
        </div>
      </div>
      <span className={`pk-badge ${fired ? 'pk-badge-success' : ''}`}>
        {fired ? <><CheckCircle size={11} /> true</> : <><Lock size={11} /> false</>}
      </span>
    </div>
  );
}
